import { useState } from "react";
import { Link } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import { Toaster, toast } from "sonner";
import { ArrowLeft, Loader2, Check, Drama, Clapperboard, HeartHandshake } from "lucide-react";
import { ContentProvider, useContent } from "@/context/ContentContext";
import { Analytics } from "@/components/site/Analytics";
import { Grain } from "@/components/site/Grain";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { createSubmission } from "@/lib/api";

const ROLES = [
  { key: "Acteur / Actrice", icon: Drama, desc: "Jouer devant la caméra dans nos fictions et courts métrages." },
  { key: "Technicien·ne", icon: Clapperboard, desc: "Image, son, lumière, montage — rejoignez l'équipe technique." },
  { key: "Bénévole", icon: HeartHandshake, desc: "Aider sur les tournages, événements et le festival." },
];

const RejoindreBody = () => {
  const { content } = useContent();
  const [role, setRole] = useState(ROLES[0].key);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Nom et email requis."); return; }
    setBusy(true);
    try {
      await createSubmission({ type: "casting", role, refTitle: "Candidature spontanée", name: form.name, email: form.email, phone: form.phone, message: form.message });
      setDone(true); toast.success("Candidature envoyée !");
    } catch { toast.error("Échec de l'envoi. Réessayez."); }
    finally { setBusy(false); }
  };

  return (
    <div className="App bg-[#050505] min-h-screen flex flex-col">
      <Grain />
      <Toaster theme="dark" position="bottom-right" />
      <Navbar />
      <main className="flex-1 pt-32 md:pt-40 pb-24" data-testid="rejoindre-page">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <Reveal className="mb-12 max-w-2xl">
            <Link to="/" className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-secondary2 hover:text-electric transition-colors mb-8"><ArrowLeft size={14} /> Accueil</Link>
            <div className="flex items-center gap-3 mb-5"><span className="h-px w-10 bg-electric" /><span className="text-[11px] tracking-[0.35em] uppercase text-electric">Rejoindre l'aventure</span></div>
            <h1 className="font-serif text-5xl md:text-7xl text-white tracking-tight leading-[0.95] mb-6">Devenez acteur du cinéma.</h1>
            <p className="text-base md:text-lg text-secondary2 font-light leading-relaxed">Acteur, technicien ou bénévole — quel que soit votre profil, il y a une place pour vous chez KARAMS STUDIO. Choisissez votre rôle et présentez-vous.</p>
          </Reveal>

          {done ? (
            <Reveal className="rounded-2xl border border-electric/25 bg-surface/50 p-10 text-center max-w-xl" data-testid="rejoindre-success">
              <div className="mx-auto h-16 w-16 rounded-full bg-electric/15 border border-electric/40 flex items-center justify-center mb-6"><Check size={28} className="text-electric" /></div>
              <h2 className="font-serif text-3xl text-white mb-3">Merci !</h2>
              <p className="text-secondary2 font-light">Votre candidature a bien été envoyée. Vous recevrez un e-mail de confirmation et notre équipe vous recontactera bientôt.</p>
              <Link to="/" className="mt-7 inline-flex px-7 py-3 rounded-full bg-electric text-black text-[12px] tracking-[0.12em] uppercase font-semibold hover:bg-white transition-colors">Retour à l'accueil</Link>
            </Reveal>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10">
              <div className="space-y-3">
                {ROLES.map((r) => (
                  <button key={r.key} data-testid={`rejoindre-role-${r.key}`} onClick={() => setRole(r.key)}
                    className={`w-full text-left rounded-xl border p-5 flex gap-4 transition-colors ${role === r.key ? "border-electric bg-electric/5" : "border-white/10 hover:border-white/25"}`}>
                    <r.icon size={26} className={role === r.key ? "text-electric" : "text-white/60"} />
                    <div><div className="font-serif text-xl text-white">{r.key}</div><p className="text-sm text-secondary2 font-light mt-1">{r.desc}</p></div>
                  </button>
                ))}
              </div>
              <Reveal delay={0.1}>
                <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-7 space-y-4">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-electric">Candidature — {role}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <label className="block"><span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">Nom complet</span>
                      <input data-testid="rejoindre-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-2 w-full bg-[#0e0e0e] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:border-electric outline-none" /></label>
                    <label className="block"><span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">Téléphone</span>
                      <input data-testid="rejoindre-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-2 w-full bg-[#0e0e0e] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:border-electric outline-none" /></label>
                  </div>
                  <label className="block"><span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">Email</span>
                    <input data-testid="rejoindre-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-2 w-full bg-[#0e0e0e] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:border-electric outline-none" /></label>
                  <label className="block"><span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">Parlez-nous de vous (expérience, motivation)</span>
                    <textarea data-testid="rejoindre-message" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} className="mt-2 w-full bg-[#0e0e0e] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:border-electric outline-none resize-y" /></label>
                  <button type="submit" disabled={busy} data-testid="rejoindre-submit" className="w-full inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-electric text-black text-[12px] tracking-[0.12em] uppercase font-semibold hover:bg-white transition-colors disabled:opacity-60">
                    {busy ? <Loader2 size={15} className="animate-spin" /> : null} Envoyer ma candidature
                  </button>
                </form>
              </Reveal>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default function RejoindrePage() {
  return (
    <ContentProvider>
      <ReactLenis root options={{ lerp: 0.08, duration: 1.2, smoothWheel: true }}>
        <Analytics />
        <RejoindreBody />
      </ReactLenis>
    </ContentProvider>
  );
}
